import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, RotateCcw, Home, Star, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import axios from "axios";

import { logger } from "@/utils/logger";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WinnerAnnouncement = ({ 
  winner, 
  onPlayAgain, 
  onBackToMenu,
  isVisible,
  playerXName = "X",
  playerOName = "O",
  playerType = "ai",
  mode = "classic",
  sosScores = [0, 0],
  sosPlayerNames = ["Player 1", "Player 2"],
  sosPlayerCount = 2,
  boardSize,
  moveCount,
  gameStartTime,
}) => {
  const { width, height } = useWindowSize();
  const [showStars, setShowStars] = useState(false);
  const [confettiPhase, setConfettiPhase] = useState(0);
  const [saveName, setSaveName] = useState("");
  const [scoreSaved, setScoreSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isVisible && winner !== "draw") {
      setConfettiPhase(1);
      setShowStars(true);
      const t1 = setTimeout(() => setConfettiPhase(2), 1500);
      const t2 = setTimeout(() => setConfettiPhase(3), 3000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else {
      setConfettiPhase(0);
      setShowStars(false);
    }
  }, [isVisible, winner]);

  useEffect(() => {
    if (isVisible) { setSaveName(""); setScoreSaved(false); setSaving(false); }
  }, [isVisible]);

  const handleSaveScore = async () => {
    if (!saveName.trim() || saving) return;
    setSaving(true);
    try {
      const duration = gameStartTime ? Math.floor((Date.now() - gameStartTime) / 1000) : 0;
      await axios.post(`${API}/game/record`, {
        player_name: saveName.trim(),
        mode,
        board_size: boardSize,
        opponent_type: playerType,
        winner,
        moves_count: moveCount || 0,
        duration_seconds: duration,
        score: mode === "sos" ? Math.max(...sosScores.slice(0, sosPlayerCount)) : undefined,
      });
      setScoreSaved(true);
    } catch (e) {
      logger.error("Failed to save score:", e);
    }
    setSaving(false);
  };

  const getWinnerName = () => {
    if (winner === "draw") return null;
    if (mode === "sos") {
      const match = winner.match(/player(\d+)/);
      if (match) {
        const idx = parseInt(match[1]) - 1;
        return sosPlayerNames[idx] || `Player ${idx + 1}`;
      }
    }
    if (playerType === "pvp") {
      return winner === "X" ? playerXName : playerOName;
    }
    return winner === "X" ? "Kamu" : "AI";
  };

  const getMessage = () => {
    if (winner === "draw") return "Seri!";
    const name = getWinnerName();
    return `${name} Menang!`;
  };

  const getSubMessage = () => {
    if (winner === "draw") return "Pertandingan seru! Main lagi?";
    if (mode === "sos") {
      const scoreStr = sosScores.slice(0, sosPlayerCount).join(" - ");
      return `Skor Akhir: ${scoreStr}`;
    }
    if (playerType === "ai") {
      return winner === "X" ? "Kamu hebat sekali!" : "Coba lagi, pasti bisa!";
    }
    return "Permainan yang seru!";
  };

  const getWinnerColor = () => {
    if (winner === "draw") return "text-gold-light";
    if (mode === "sos") {
      const match = winner.match(/player(\d+)/);
      if (match) {
        const colors = ["text-cyan-400", "text-pink-400", "text-purple-400", "text-yellow-400"];
        return colors[parseInt(match[1]) - 1] || "text-gold";
      }
    }
    return winner === "X" ? "text-gold" : "text-orange-400";
  };

  const CONFETTI_COLORS_1 = ["#FFD700", "#FF6B6B", "#4ECDC4", "#A855F7", "#FF69B4", "#48DBFB", "#FECA57", "#FF6B35"];
  const CONFETTI_COLORS_2 = ["#FFD700", "#FFA500", "#FF4500", "#FF1493", "#FFD700"];

  // Floating star emojis
  const floatingEmojis = ["⭐", "🌟", "✨", "🎉", "🏆", "🎊", "💫", "🥳"];

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Phase 1: Big burst */}
          {winner !== "draw" && confettiPhase >= 1 && (
            <Confetti
              width={width} height={height}
              colors={CONFETTI_COLORS_1}
              recycle={false}
              numberOfPieces={500}
              gravity={0.15}
              initialVelocityX={15}
              initialVelocityY={30}
              tweenDuration={5000}
              confettiSource={{ x: width / 2, y: -20, w: 10, h: 10 }}
            />
          )}

          {/* Phase 2: Side cannons */}
          {winner !== "draw" && confettiPhase >= 2 && (
            <>
              <Confetti
                width={width} height={height}
                colors={CONFETTI_COLORS_2}
                recycle={false}
                numberOfPieces={200}
                gravity={0.2}
                initialVelocityX={20}
                initialVelocityY={15}
                confettiSource={{ x: 0, y: height * 0.5, w: 10, h: 10 }}
              />
              <Confetti
                width={width} height={height}
                colors={CONFETTI_COLORS_2}
                recycle={false}
                numberOfPieces={200}
                gravity={0.2}
                initialVelocityX={-20}
                initialVelocityY={15}
                confettiSource={{ x: width, y: height * 0.5, w: 10, h: 10 }}
              />
            </>
          )}

          {/* Phase 3: Gentle rain */}
          {winner !== "draw" && confettiPhase >= 3 && (
            <Confetti
              width={width} height={height}
              colors={["#FFD70080", "#FF6B6B80", "#4ECDC480", "#A855F780"]}
              recycle={true}
              numberOfPieces={80}
              gravity={0.08}
              wind={0.01}
            />
          )}

          {/* Draw: small sad confetti */}
          {winner === "draw" && (
            <Confetti
              width={width} height={height}
              colors={["#FFD70060", "#FFFFFF40"]}
              recycle={false}
              numberOfPieces={50}
              gravity={0.05}
            />
          )}

          {/* Floating emoji stars */}
          {showStars && winner !== "draw" && (
            <div className="fixed inset-0 pointer-events-none z-[55]">
              {floatingEmojis.map((emoji, i) => (
                <motion.div
                  key={i}
                  className="absolute text-3xl sm:text-4xl"
                  style={{ left: `${10 + (i * 11)}%` }}
                  initial={{ y: height + 50, opacity: 0, rotate: 0, scale: 0.5 }}
                  animate={{
                    y: [height, height * 0.3 - (i * 30), -100],
                    opacity: [0, 1, 0],
                    rotate: [0, 360 * (i % 2 === 0 ? 1 : -1)],
                    scale: [0.5, 1.3, 0.5],
                  }}
                  transition={{
                    duration: 3 + (i * 0.3),
                    delay: 0.2 + (i * 0.15),
                    ease: "easeOut",
                  }}
                >
                  {emoji}
                </motion.div>
              ))}
            </div>
          )}
          
          {/* Modal */}
          <motion.div
            data-testid="winner-announcement"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={onPlayAgain}
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass-card-strong rounded-2xl p-6 sm:p-8 border-2 border-gold/30 text-center max-w-sm w-full relative overflow-hidden"
              style={{ boxShadow: winner !== "draw" ? '0 0 80px rgba(255,215,0,0.3), 0 0 160px rgba(255,107,53,0.15)' : '0 0 40px rgba(255,215,0,0.1)' }}
            >
              {/* Background shimmer */}
              {winner !== "draw" && (
                <motion.div
                  className="absolute inset-0 opacity-10"
                  style={{ background: 'radial-gradient(circle at center, #FFD700, transparent 70%)' }}
                  animate={{ scale: [1, 1.5, 1], opacity: [0.05, 0.15, 0.05] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}

              {/* Trophy with bounce */}
              <motion.div
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
                className="relative z-10"
              >
                {winner !== "draw" ? (
                  <motion.div
                    animate={{ rotate: [0, -10, 10, -10, 10, 0], scale: [1, 1.1, 1] }}
                    transition={{ duration: 1, delay: 0.5 }}
                  >
                    <Trophy className={`w-20 h-20 mx-auto mb-4 ${getWinnerColor()}`} style={{ filter: 'drop-shadow(0 0 20px rgba(255,215,0,0.5))' }} />
                  </motion.div>
                ) : (
                  <div className="text-5xl mb-4">🤝</div>
                )}
              </motion.div>

              {/* Winner text with scale animation */}
              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className={`font-fredoka text-3xl sm:text-4xl font-bold mb-2 ${getWinnerColor()} relative z-10`}
              >
                {getMessage()}
              </motion.h2>

              {/* Stars row */}
              {winner !== "draw" && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: "spring" }}
                  className="flex justify-center gap-1 mb-2 relative z-10"
                >
                  {[0, 1, 2].map(i => (
                    <motion.div key={i}
                      initial={{ rotate: 0, scale: 0 }}
                      animate={{ rotate: 360, scale: 1 }}
                      transition={{ delay: 0.5 + i * 0.15, type: "spring", stiffness: 200 }}
                    >
                      <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" style={{ filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.6))' }} />
                    </motion.div>
                  ))}
                </motion.div>
              )}

              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.45 }}
                className="font-fredoka text-gold-light/80 mb-4 relative z-10"
              >
                {getSubMessage()}
              </motion.p>

              {/* Save Score */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="relative z-10 mb-4 w-full"
              >
                {!scoreSaved ? (
                  <div className="flex gap-2">
                    <input
                      data-testid="winner-save-name-input"
                      type="text"
                      value={saveName}
                      onChange={(e) => setSaveName(e.target.value)}
                      placeholder="Nama kamu..."
                      maxLength={50}
                      className="flex-1 glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none focus:border-gold/40"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveScore()}
                    />
                    <Button onClick={handleSaveScore} disabled={!saveName.trim() || saving}
                      className="font-fredoka font-bold text-brown-dark px-4 rounded-xl" style={{ background: '#4ade80' }}
                      data-testid="winner-save-score-btn">
                      <Save className="w-4 h-4 mr-1" />{saving ? '...' : 'Simpan'}
                    </Button>
                  </div>
                ) : (
                  <p className="text-green-400 font-fredoka text-sm text-center" data-testid="winner-score-saved">Skor tersimpan!</p>
                )}
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.55 }}
                className="flex flex-col sm:flex-row gap-3 relative z-10"
              >
                <Button
                  data-testid="reset-game-button"
                  onClick={onPlayAgain}
                  className="flex-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-white hover:opacity-90 font-fredoka font-bold rounded-xl shadow-lg h-12 text-base"
                >
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Main Lagi!
                </Button>
                <Button
                  data-testid="back-to-menu-btn"
                  onClick={onBackToMenu}
                  variant="outline"
                  className="flex-1 glass-card border-gold/30 text-gold hover:bg-gold/10 font-fredoka font-bold rounded-xl h-12 text-base"
                >
                  <Home className="w-5 h-5 mr-2" />
                  Menu
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WinnerAnnouncement;
